import type { AdministrativeNote, AdministrativeNoteReferral } from "./administrative-note"
import type {
	BenefitFundLoan,
	BenefitFundLottery,
	BenefitFundMember,
	BenefitFundMembershipRequest
} from "./benefit-fund"
import type { Chat, ChatMessage } from "./chat"
import type { Company } from "./company"
import type { FoodReservation } from "./food"
import type { Image } from "./image"
import type { KanbanActivity, KanbanChecklistItem, KanbanTask, KanbanTaskComment, KanbanTaskMember } from "./kanban"
import type { Post } from "./post"
import type { ProjectUser, ProjectUserComment } from "./project"
import type { SurveyAnswer } from "./survey"

export type UserType = "user" | "company-user" | "admin" | "developer" | "blogger" | "support"
export type UserStatus = "enable" | "disable" | "block"

export type User = {
	id: number | null
	staff_number: string
	staff_number_with_prefix: string
	mobile: string
	mobile_verified: boolean
	email: string
	email_verified: boolean
	company_id?: number
	company_user_id?: number
	firstname: string
	lastname: string
	birthday: string
	image_id: number | null
	image_path: string | null
	image_thumb1: string | null
	image_thumb2: string | null
	type: UserType
	status: UserStatus
	password: string
	token: string | null
	session?: UserSession
	created: string
	updated: string
	last_login: string
	last_activity: string | null
	code?: string
	account_number: string
	post: string
	organization_unit?: string
	organization_unit_id?: number | null
	organization_unit_name?: { [key: string]: string } | null
	organization_unit_slug?: string
	staff_number_prefix?: string | null
	supervisor: string
	supervisor_id: number | null
	supervisor_firstname: string | null
	supervisor_lastname: string | null
	supervisor_staff_number: string | null
	supervisor_staff_number_with_prefix: string | null
	supervisor_supervisor_organization_unit_id: number | null
	salon: string
	telephone: string | null
	address: string | null
	residence_status: string | null
	residence_description: string | null
	national_code: string | null
	is_land_owner: "yes" | "no" | null
	is_married: "yes" | "no" | null
	has_housing_loan: "yes" | "no" | null
	city_of_residence: string | null
	billing_address_id?: number | null
	shipping_address_id?: number | null
	is_demo: boolean
	is_company_demo: boolean
	need_change_password: boolean
	hire_date: string | null
	is_tester: boolean
	changePassword?: any
	role_id?: number
	role_name?: string
	ntfy_username?: string
	ntfy_password?: string
	ntfy_topic: string | null
	allow_full_access?: boolean
	allow_user_show?: boolean
	allow_user_manage?: boolean
	allow_payroll_show?: boolean
	allow_payroll_manage?: boolean
	allow_performance_review_show?: boolean
	allow_performance_review_manage?: boolean
	allow_career_show?: boolean
	allow_career_manage?: boolean
	allow_career_request_show?: boolean
	allow_career_request_manage?: boolean
	allow_attendance_show?: boolean
	allow_attendance_manage?: boolean
	allow_project_show?: boolean
	allow_project_manage?: boolean
	allow_custom_page_show?: boolean
	allow_custom_page_show_ids?: string[]
	allow_custom_page_manage?: boolean
	allow_custom_page_manage_ids?: string[]
	allow_category_show?: boolean
	allow_category_show_types?: string[]
	allow_category_manage?: boolean
	allow_category_manage_types?: string[]
	allow_disciplinary_note_show?: boolean
	allow_disciplinary_note_show_types?: string[]
	allow_disciplinary_note_manage?: boolean
	allow_disciplinary_note_manage_types?: string[]
	allow_food_show?: boolean
	allow_food_manage?: boolean
	allow_chat_show?: boolean
	allow_chat_manage?: boolean
	allow_mail_show?: boolean
	allow_mail_manage?: boolean
	allow_survey_show?: boolean
	allow_survey_manage?: boolean
	allow_cms_form_show?: boolean
	allow_cms_form_manage?: boolean
	allow_cms_form_submission_show?: boolean
	allow_cms_form_submission_show_slugs?: string[]
	allow_cms_form_submission_manage?: boolean
	allow_cms_form_submission_manage_slugs?: string[]
	allow_announcement_show?: boolean
	allow_announcement_manage?: boolean
	allow_dev_ticket_show?: boolean
	allow_dev_ticket_manage?: boolean
	allow_contacts_show?: boolean
	allow_contacts_manage?: boolean
	allow_crm_deals_show?: boolean
	allow_crm_deals_manage?: boolean
	allow_crm_activities_show?: boolean
	allow_crm_activities_manage?: boolean
	allow_crm_settings_manage?: boolean
	allow_cms_show?: boolean
	allow_cms_show_types?: string[]
	allow_cms_manage?: boolean
	allow_cms_manage_types?: string[]
	allow_cms_post_types_manage?: boolean
	company?: Company
	subordinate_count?: number
	workspace_locations: WorkspaceLocation[]
	today_attendance?: UserAttendance | null
	onboarding_stage?: string | null
}

export type UserSession = {
	id: string
	user_id: number
	refresh_token?: string
	ntfy_token?: string
	device_type: string
	device_model: string
	browser_version: string
	browser_model: string
	ip_address: string
	expires_at: string
	created: string
	used: string
}

export type WorkspaceLocation = {
	id: number
	user_id: number
	name: string
	longitude: number
	latitude: number
	radius: number
	created: string
	updated: string
}

export type UserAttendance = {
	id: number
	company_id: number
	user_id: number
	attendance_date: string
	check_in_time: string | null
	check_out_time: string | null
	status?: "present" | "absent" | "late" | "on_leave"
	face_recognition_verified: boolean
	workspace_name?: string
	created: string
	updated: string
}

export type ConvertObjectToUserType =
	| "image"
	| "user"
	| "project-user"
	| "chat"
	| "chat-message"
	| "kanban-task-member"
	| "kanban-task"
	| "kanban-checklist-item"
	| "kanban-task-comment"
	| "kanban-activity"
	| "project-user-comment"
	| "comment"
	| "post"
	| "administrative-note"
	| "administrative-note-referral-sender"
	| "administrative-note-referral-recipient"
	| "food-reservation"
	| "survey-answer"
	| "benefit-fund-membership-request"
	| "benefit-fund-member"
	| "benefit-fund-lottery-winner"
	| "benefit-fund-lottery-drawn-by"
	| "benefit-fund-loan"

export const convertObjectToUser = (o: unknown, type: ConvertObjectToUserType, me: boolean = false): User | null => {
	const userStore = useUserStore()

	if (type == "user") return o as User

	let t

	if (type == "benefit-fund-membership-request") t = o as BenefitFundMembershipRequest
	if (type == "benefit-fund-member") t = o as BenefitFundMember
	if (t) {
		return {
			id: t.user_id,
			firstname: t.firstname || "",
			lastname: t.lastname || "",
			staff_number: t.staff_number || "",
			staff_number_with_prefix: t.staff_number_with_prefix || "",
			last_activity: t.last_activity || "",
			image_id: t.image_id || null,
			image_path: t.image_path,
			image_thumb1: t.image_thumb1,
			image_thumb2: t.image_thumb2
		} as User
	}

	if (type == "benefit-fund-lottery-winner") t = o as BenefitFundLottery
	if (t) {
		return {
			id: t.winner_id,
			firstname: t.winner_firstname || "",
			lastname: t.winner_lastname || "",
			staff_number: t.winner_staff_number || "",
			staff_number_with_prefix: t.winner_staff_number_with_prefix || "",
			last_activity: t.winner_last_activity || "",
			image_id: t.winner_image_id || null,
			image_path: t.winner_image_path,
			image_thumb1: t.winner_image_thumb1,
			image_thumb2: t.winner_image_thumb2
		} as User
	}

	if (type == "benefit-fund-lottery-drawn-by") t = o as BenefitFundLottery
	if (t) {
		return {
			id: t.winner_id,
			firstname: t.drawn_by_firstname || "",
			lastname: t.drawn_by_lastname || "",
			staff_number: t.drawn_by_staff_number || "",
			staff_number_with_prefix: t.drawn_by_staff_number_with_prefix || "",
			last_activity: t.drawn_by_last_activity || "",
			image_id: t.drawn_by_image_id || null,
			image_path: t.drawn_by_image_path,
			image_thumb1: t.drawn_by_image_thumb1,
			image_thumb2: t.drawn_by_image_thumb2
		} as User
	}

	if (type == "benefit-fund-loan") t = o as BenefitFundLoan
	if (t) {
		return {
			id: t.user_id,
			firstname: t.firstname || "",
			lastname: t.lastname || "",
			staff_number: t.staff_number || "",
			staff_number_with_prefix: t.staff_number_with_prefix || "",
			last_activity: t.last_activity || "",
			image_id: t.image_id || null,
			image_path: t.image_path,
			image_thumb1: t.image_thumb1,
			image_thumb2: t.image_thumb2
		} as User
	}

	if (type == "image") {
		t = o as Image
		return {
			image_id: t.id,
			image_path: t.path,
			image_thumb1: t.path_thumb1,
			image_thumb2: t.path_thumb2
		} as User
	}

	if (type == "project-user") t = o as ProjectUser
	if (type == "chat") {
		t = o as Chat
		if ((userStore.user?.id == t.user_id) != me) {
			t = null
		}
	}
	if (type == "chat-message") t = o as ChatMessage
	if (type == "kanban-task-member") t = o as KanbanTaskMember
	if (type == "kanban-checklist-item") t = o as KanbanChecklistItem
	if (type == "kanban-task-comment") t = o as KanbanTaskComment
	if (type == "kanban-activity") t = o as KanbanActivity
	if (type == "project-user-comment") t = o as ProjectUserComment
	if (type == "post") t = o as Post
	if (type == "food-reservation") t = o as FoodReservation
	if (type == "survey-answer") t = o as SurveyAnswer
	if (t) {
		return {
			id: t.user_id,
			firstname: t.user_firstname || "",
			lastname: t.user_lastname || "",
			staff_number: t.user_staff_number || "",
			staff_number_with_prefix: t.user_staff_number_with_prefix || "",
			last_activity: t.user_last_activity || "",
			image_id: t.user_image_id || null,
			image_path: t.user_image_path,
			image_thumb1: t.user_image_thumb1,
			image_thumb2: t.user_image_thumb2
		} as User
	}

	if (type == "chat") {
		t = o as Chat
		if ((userStore.user?.id == t.receiver_id) != me) {
			t = null
		}
		if (t) {
			return {
				id: t.receiver_id,
				firstname: t.receiver_firstname,
				lastname: t.receiver_lastname,
				staff_number: t.receiver_staff_number,
				staff_number_with_prefix: t.receiver_staff_number_with_prefix,
				last_activity: t.receiver_last_activity || "",
				image_id: t.receiver_image_id,
				image_path: t.receiver_image_path,
				image_thumb1: t.receiver_image_thumb1,
				image_thumb2: t.receiver_image_thumb2
			} as User
		}
	}

	if (type == "kanban-task") t = o as KanbanTask
	if (type == "administrative-note") t = o as AdministrativeNote
	if (t) {
		return {
			id: t.creator_id,
			firstname: t.creator_firstname,
			lastname: t.creator_lastname,
			staff_number: t.creator_staff_number,
			staff_number_with_prefix: t.creator_staff_number_with_prefix,
			last_activity: t.creator_last_activity || "",
			image_id: t.creator_image_id,
			image_path: t.creator_image_path,
			image_thumb1: t.creator_image_thumb1,
			image_thumb2: t.creator_image_thumb2
		} as User
	}

	if (type == "administrative-note-referral-sender") t = o as AdministrativeNoteReferral
	if (t) {
		return {
			id: t.sender_id,
			firstname: t.sender_firstname,
			lastname: t.sender_lastname,
			staff_number: t.sender_staff_number,
			staff_number_with_prefix: t.sender_staff_number_with_prefix,
			last_activity: t.sender_last_activity || "",
			image_id: t.sender_image_id,
			image_path: t.sender_image_path,
			image_thumb1: t.sender_image_thumb1,
			image_thumb2: t.sender_image_thumb2
		} as User
	}

	if (type == "administrative-note-referral-recipient") t = o as AdministrativeNoteReferral
	if (t) {
		return {
			id: t.recipient_id,
			firstname: t.recipient_firstname,
			lastname: t.recipient_lastname,
			staff_number: t.recipient_staff_number,
			staff_number_with_prefix: t.recipient_staff_number_with_prefix,
			last_activity: t.recipient_last_activity || "",
			image_id: t.recipient_image_id,
			image_path: t.recipient_image_path,
			image_thumb1: t.recipient_image_thumb1,
			image_thumb2: t.recipient_image_thumb2
		} as User
	}

	return null
}

export const getUserDisplayName = (o: unknown, type: ConvertObjectToUserType = "user", me: boolean = false) => {
	const u = convertObjectToUser(o, type, me)
	if (!u) return ""
	return String((u.firstname || "") + " " + (u.lastname || "")).trim() || u.staff_number_with_prefix || ""
}

export const isUserOnline = (o: unknown, type: ConvertObjectToUserType = "user", me: boolean = false) => {
	const moment = useMoment()
	const u = convertObjectToUser(o, type, me)
	if (!u) return false
	if (!u.last_activity) return false
	return moment.utc(u.last_activity).isAfter(moment.utc().subtract(5, "minute"))
}
